import { isNotNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { useCallback, useMemo } from "react";

export type NumberFormatKind = "compact" | "thousand-separator";
export type NumberFormatter = (value: number) => string;

export type UseNumberFormatterOptions = {
	format: NumberFormatKind;
	culture: string;
	maxValue?: number;
};

export function useNumberFormatter(
	options: UseNumberFormatterOptions
): NumberFormatter {
	const {
		culture,
		format,
		maxValue
	} = options;

	const formatter = useMemo(
		(): Intl.NumberFormat => {
			switch (format) {
				case "compact":
					return new Intl.NumberFormat(
						culture,
						{
							notation: "compact",
							maximumFractionDigits: 1,
							compactDisplay: "short"
						} satisfies Intl.NumberFormatOptions
					);
				case "thousand-separator":
					return new Intl.NumberFormat(
						culture,
						{
							style: "decimal",
							maximumFractionDigits: 2
						} satisfies Intl.NumberFormatOptions
					);
				default:
					never(format);
			}
		},
		[culture, format]
	);

	const formatValue = useCallback(
		(value: number, didApplyLimit: boolean): string => {
			return `${formatter.format(value)}${didApplyLimit ? "+" : ""}`;
		},
		[formatter]
	);

	return useCallback(
		(value: number): string => {
			if (isNotNil(maxValue) && value > maxValue) {
				return formatValue(maxValue, true);
			} else {
				return formatValue(value, false);
			}
		},
		[formatValue, maxValue]
	);
}